/**
 * NotificationsPage - Full-page notifications view
 *
 * Displays notifications as ItemTile-style cards in a responsive grid.
 * Parent item tiles (white bg) are ordered by notification date/time UTC.
 * Sub-item tiles (light grey bg) are grouped next to their parent,
 * ordered by their own notification date/time UTC.
 * Clicking a tile navigates to the item/sub-item.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Check,
  X,
  CheckSquare,
  DollarSign,
  FileText,
  Calendar,
} from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Card } from '@/components/ui';
import { useNotificationStore, useAuthStore, useUIStore, useItemStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { formatRelativeDate, capitalize } from '@perfin/shared';
import { doc as firestoreDoc, onSnapshot as firestoreOnSnapshot, getFirestore } from 'firebase/firestore';
import { NotificationsFilterBar } from './NotificationsFilterBar';
import type { Notification } from '@perfin/shared';

// ============================================================================
// CONSTANTS
// ============================================================================

const TYPE_ICON_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  task: { icon: CheckSquare, color: 'text-blue-600' },
  transaction: { icon: DollarSign, color: 'text-green-600' },
  note: { icon: FileText, color: 'text-amber-600' },
  keyDate: { icon: Calendar, color: 'text-purple-600' },
};

// ============================================================================
// HELPERS
// ============================================================================

/** Parse "ParentName > SubItemName" into parts */
function parseNotificationName(notification: Notification): {
  isSubItem: boolean;
  displayName: string;
  parentName: string | null;
} {
  if (!notification.subItemId) {
    return { isSubItem: false, displayName: notification.itemName, parentName: null };
  }
  const parts = notification.itemName.split(' > ');
  if (parts.length >= 2) {
    return {
      isSubItem: true,
      parentName: parts[0],
      displayName: parts.slice(1).join(' > '),
    };
  }
  return { isSubItem: true, displayName: notification.itemName, parentName: null };
}

/** Get createdAt as millis for sorting */
function getCreatedAtMs(n: Notification): number {
  if (!n.createdAt) return 0;
  if (typeof (n.createdAt as { toMillis?: () => number }).toMillis === 'function') {
    return (n.createdAt as { toMillis: () => number }).toMillis();
  }
  if (typeof (n.createdAt as { toDate?: () => Date }).toDate === 'function') {
    return (n.createdAt as { toDate: () => Date }).toDate().getTime();
  }
  return 0;
}

/** Build navigation path for a notification */
function getNavPath(notification: Notification, returnPath: string): string {
  const basePath = notification.itemType === 'keyDate' ? 'dates' : `${notification.itemType}s`;
  const itemPath = `/${basePath}/${notification.itemId}`;
  const params = new URLSearchParams({
    from: 'notifications',
    returnTo: returnPath,
  });
  if (notification.subItemId) {
    params.set('subItem', notification.subItemId);
  }
  return `${itemPath}?${params.toString()}`;
}

/**
 * Group and order notifications:
 * 1. Separate parent and sub-item notifications
 * 2. Group sub-items by their parent itemId
 * 3. Sort parent groups by earliest notification createdAt
 * 4. Within each group, sub-items sorted by createdAt
 * 5. Flatten: [parent1, sub1a, sub1b, parent2, ...]
 *    If a parent has no notification of its own, its sub-items still appear
 *    in their group (ordered by the earliest sub-item createdAt).
 */
function groupAndOrderNotifications(notifications: Notification[]): Notification[] {
  const parentNotifsByItem = new Map<string, Notification[]>();
  const subNotifsByParent = new Map<string, Notification[]>();

  for (const n of notifications) {
    if (n.subItemId) {
      const subs = subNotifsByParent.get(n.itemId) || [];
      subs.push(n);
      subNotifsByParent.set(n.itemId, subs);
    } else {
      const parents = parentNotifsByItem.get(n.itemId) || [];
      parents.push(n);
      parentNotifsByItem.set(n.itemId, parents);
    }
  }

  // Sort within each group by createdAt
  for (const parents of parentNotifsByItem.values()) {
    parents.sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a)); // most recent first
  }
  for (const subs of subNotifsByParent.values()) {
    subs.sort((a, b) => getCreatedAtMs(a) - getCreatedAtMs(b));
  }

  // Build groups: each group = { sortKey, parents[], subs[] }
  interface Group {
    sortKey: number;
    parents: Notification[];
    subs: Notification[];
  }

  const groupMap = new Map<string, Group>();

  // Add parent notification groups
  for (const [itemId, parents] of parentNotifsByItem) {
    groupMap.set(itemId, {
      sortKey: getCreatedAtMs(parents[0]), // most recent parent
      parents,
      subs: subNotifsByParent.get(itemId) || [],
    });
    subNotifsByParent.delete(itemId);
  }

  // Add orphan sub-item groups (sub-items whose parent has no notification)
  for (const [itemId, subs] of subNotifsByParent) {
    groupMap.set(itemId, {
      sortKey: subs.length > 0 ? getCreatedAtMs(subs[0]) : 0,
      parents: [],
      subs,
    });
  }

  // Sort groups by sortKey (most recent first)
  const sortedGroups = Array.from(groupMap.values()).sort((a, b) => b.sortKey - a.sortKey);

  // Flatten: all parents first, then all subs for each group
  const result: Notification[] = [];
  for (const group of sortedGroups) {
    result.push(...group.parents);
    result.push(...group.subs);
  }

  return result;
}

// ============================================================================
// NOTIFICATION TILE COMPONENT
// ============================================================================

interface NotificationTileProps {
  notification: Notification;
  onClear: (notification: Notification) => void;
  onToggleComplete: (notification: Notification) => void;
  onNavigate: (notification: Notification) => void;
}

function NotificationTile({ notification, onClear, onToggleComplete, onNavigate }: NotificationTileProps) {
  const { isSubItem, displayName, parentName } = parseNotificationName(notification);
  const config = TYPE_ICON_CONFIG[notification.itemType] || TYPE_ICON_CONFIG.task;
  const Icon = config.icon;
  const isTask = notification.itemType === 'task';
  const isCompleted = notification.itemStatus === 'complete';

  const handleTileClick = () => {
    onNavigate(notification);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear(notification);
  };

  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleComplete(notification);
  };

  return (
    <div
      onClick={handleTileClick}
      className={clsx(
        'rounded-xl border hover:shadow-md transition-shadow overflow-hidden cursor-pointer',
        isSubItem
          ? 'bg-gray-50 border-gray-200'
          : 'bg-white border-gray-200'
      )}
    >
      <div className="p-4">
        {/* Row 1: Type icon + action buttons */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={clsx('h-4 w-4 flex-shrink-0', config.color)} />
            {notification.recurrence && (
              <span className="text-xs text-blue-600 font-medium">
                {capitalize(notification.recurrence)}
              </span>
            )}
            {notification.choreWheel && !isSubItem && (
              <span className="text-xs text-white bg-purple-600 px-1.5 py-0.5 rounded font-medium">
                Chore
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Check: toggle task complete/incomplete (tasks only) */}
            {isTask && (
              <button
                onClick={handleToggleComplete}
                className={clsx(
                  'p-1 rounded transition-colors',
                  isCompleted
                    ? 'text-green-600 hover:bg-green-50'
                    : 'text-gray-400 hover:bg-gray-100'
                )}
                title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
              >
                <Check className="h-4 w-4" strokeWidth={3} />
              </button>
            )}
            {/* X: clear this notification */}
            <button
              onClick={handleClear}
              className="p-1 rounded transition-colors text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Row 2-3: Name (max 2 lines) */}
        <h3 className={clsx('font-semibold line-clamp-2 mb-1', isCompleted ? 'text-gray-400 line-through' : 'text-black')}>
          {displayName}
        </h3>

        {/* Sub-item: Parent name in green (max 2 lines) */}
        {isSubItem && parentName && (
          <p className="text-xs text-green-600 font-medium line-clamp-2 mb-1 flex items-center gap-1">
            {notification.choreWheel && (
              <span className="text-[10px] text-white bg-purple-600 px-1 py-0.5 rounded font-medium flex-shrink-0">
                Chore
              </span>
            )}
            Parent: {parentName}
          </p>
        )}

        {/* Row 4: Date info */}
        <p className="text-sm text-black opacity-70">
          {formatRelativeDate(notification.occurrenceDate || notification.dueDate)}
        </p>

        {/* Row 5: Notification message */}
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
          {notification.message}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function NotificationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuthStore();
  const { notifications, subscribeToNotifications, clearNotification, clearNotificationsByIds } = useNotificationStore();
  const { editItem, editSubItem } = useItemStore();
  const { categoryFilters, pageFilters } = useUIStore();
  const toast = useToast();
  const { confirmWarning } = useConfirm();

  const returnPath = searchParams.get('from') || '/';

  // Subscribe to notifications (wait for profile to ensure Firestore permissions work)
  useEffect(() => {
    if (!user?.uid || !profile?.organizationId || !profile?.role) return;
    const unsubscribe = subscribeToNotifications(user.uid, profile.organizationId, profile.role);
    return () => unsubscribe();
  }, [user?.uid, profile?.organizationId, profile?.role, subscribeToNotifications]);

  // Get active filters
  const filter = categoryFilters?.notifications ?? { categoryId: null, subCategoryId: null };
  const pFilters = pageFilters?.notifications ?? { ownerFilter: null, itemGroupFilter: null, statusFilter: null, priorityFilter: null };

  // Filter notifications based on active filters
  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      // Owner filter
      if (pFilters.ownerFilter && n.owner !== pFilters.ownerFilter) return false;
      // Item type filter (stored in itemGroupFilter)
      if (pFilters.itemGroupFilter && n.itemType !== pFilters.itemGroupFilter) return false;
      // Category filter
      if (filter.categoryId && n.category !== filter.categoryId) return false;
      // Subcategory filter
      if (filter.subCategoryId && n.subCategory !== filter.subCategoryId) return false;
      // Priority filter
      if (pFilters.priorityFilter && n.priority !== pFilters.priorityFilter) return false;
      // Status filter
      if (pFilters.statusFilter && n.itemStatus !== pFilters.statusFilter) return false;
      return true;
    });
  }, [notifications, pFilters, filter]);

  // Group and order filtered notifications
  const orderedNotifications = useMemo(
    () => groupAndOrderNotifications(filteredNotifications),
    [filteredNotifications]
  );

  const handleBack = () => {
    navigate(returnPath);
  };

  const handleClear = async (notification: Notification) => {
    if (!user) return;
    try {
      await clearNotification(notification.id, notification.recipientUserId);
    } catch {
      toast.error('Failed to clear notification');
    }
  };

  const handleToggleComplete = async (notification: Notification) => {
    if (!user) return;
    try {
      const isCompleted = notification.itemStatus === 'complete';
      const newStatus = isCompleted ? 'new' : 'complete';
      if (notification.subItemId) {
        await editSubItem(user.uid, notification.itemId, notification.subItemId, { status: newStatus });
      } else {
        await editItem(user.uid, notification.itemId, {
          status: newStatus,
          ...(isCompleted ? { lastKnownStatus: undefined } : { lastKnownStatus: notification.itemStatus }),
        });
      }
      toast.success(isCompleted ? 'Task reopened' : 'Task completed!');
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleNavigate = (notification: Notification) => {
    navigate(getNavPath(notification, returnPath));
  };

  const handleClearAll = () => {
    if (!user || filteredNotifications.length === 0) return;

    const count = filteredNotifications.length;
    const isFiltered = count < notifications.length;

    confirmWarning({
      title: isFiltered ? 'Clear Filtered Notifications' : 'Clear All Notifications',
      message: isFiltered
        ? `Are you sure you want to clear ${count} filtered notification${count === 1 ? '' : 's'}? This cannot be undone.`
        : 'Are you sure you want to clear all notifications? This cannot be undone.',
      onConfirm: async () => {
        try {
          const notifsToClear = filteredNotifications.map((n) => ({
            id: n.id,
            recipientUserId: n.recipientUserId,
          }));
          await clearNotificationsByIds(notifsToClear);
          toast.success(`${count} notification${count === 1 ? '' : 's'} cleared`);
        } catch {
          toast.error('Failed to clear notifications');
        }
      },
    });
  };

  // Cron health monitoring for "Service Disrupted" badge
  const [cronHealthy, setCronHealthy] = useState(true);

  useEffect(() => {
    const db = getFirestore();
    const healthRef = firestoreDoc(db, 'system', 'cronHealth');
    const unsub = firestoreOnSnapshot(healthRef, (snap) => {
      if (!snap.exists()) {
        setCronHealthy(false);
        return;
      }
      const data = snap.data();
      const lastHeartbeat = data?.lastHeartbeat;
      if (!lastHeartbeat) {
        setCronHealthy(false);
        return;
      }
      const heartbeatMs = typeof lastHeartbeat.toMillis === 'function' ? lastHeartbeat.toMillis() : 0;
      const threeMinutesAgo = Date.now() - 3 * 60 * 1000;
      setCronHealthy(heartbeatMs > threeMinutesAgo);
    }, () => {
      setCronHealthy(false);
    });
    return () => unsub();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div className="flex items-center gap-4 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Notifications
              {!cronHealthy && (
                <span className="text-xs font-semibold bg-red-600 text-white px-2 py-0.5 rounded">
                  Service Disrupted
                </span>
              )}
            </h1>
            <p className="text-gray-600 mt-1">
              {notifications.length === 0
                ? 'No notifications'
                : filteredNotifications.length === notifications.length
                  ? `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`
                  : `${filteredNotifications.length} of ${notifications.length} notification${notifications.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {filteredNotifications.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleClearAll}
            >
              Clear All{filteredNotifications.length < notifications.length ? ` (${filteredNotifications.length})` : ''}
            </Button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <NotificationsFilterBar />

      {/* Notifications Grid */}
      {filteredNotifications.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Bell className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-lg font-medium">
              {notifications.length === 0 ? 'No notifications' : 'No matching notifications'}
            </p>
            <p className="text-sm mt-1">
              {notifications.length === 0 ? "You're all caught up!" : 'Try adjusting your filters'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orderedNotifications.map((notification) => (
            <NotificationTile
              key={notification.id}
              notification={notification}
              onClear={handleClear}
              onToggleComplete={handleToggleComplete}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
